import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    details: '(855) 831-1992',
    subtext: 'Available 7 days a week',
  },
  {
    icon: Mail,
    title: 'Email',
    details: 'info@healthcentermedicalgroup.online',
    subtext: 'We respond within 24 hours',
  },
  {
    icon: MapPin,
    title: 'Main Office',
    details: '7100 Commerce Way Ste 182',
    subtext: 'Brentwood, TN 37027',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    details: 'Mon-Fri: 9AM - 5PM',
    subtext: 'Sat-Sun: 11AM - 4PM',
  },
];

const services = [
  'Revenue Cycle Management',
  'Practice Management',
  'Credentialing Services',
  'Compliance & HR',
  'IT Solutions',
  'Other',
];

const practiceTypes = [
  'Solo Practice',
  'Small Group (2-10 providers)',
  'Large Group (11-50 providers)',
  'Health System',
  'Hospital',
  'Other',
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    practiceType: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Your message has been sent successfully!');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-navy" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Contact Us
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Let's Start a Conversation
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed">
              Ready to transform your healthcare operations? Get in touch with our team to discuss how we can help your organization succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white" data-testid="contact-info-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20 relative z-10">
            {contactInfo.map((info) => (
              <Card 
                key={info.title}
                className="bg-white border-silver-light shadow-card"
                data-testid={`contact-info-${info.title.toLowerCase()}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-sm bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <info.icon className="w-6 h-6 text-emerald" />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy mb-1">{info.title}</h3>
                  <p className="text-navy-700 font-medium">{info.details}</p>
                  <p className="text-sm text-navy-500">{info.subtext}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-navy-50" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-manrope font-bold text-2xl md:text-3xl text-navy mb-6">
                Request a Consultation
              </h2>
              <p className="text-navy-600 mb-8">
                Fill out the form below and one of our specialists will contact you within 24 hours.
              </p>

              {isSubmitted ? (
                <Card className="bg-emerald-50 border-emerald">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-emerald mx-auto mb-4" />
                    <h3 className="font-manrope font-semibold text-navy text-xl mb-2">Thank You!</h3>
                    <p className="text-navy-600">
                      We've received your message and will be in touch within 24 hours.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">First Name *</label>
                      <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="rounded-none border-silver h-12 bg-white focus:border-navy"
                        data-testid="contact-firstname-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Last Name *</label>
                      <Input
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="rounded-none border-silver h-12 bg-white focus:border-navy"
                        data-testid="contact-lastname-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="rounded-none border-silver h-12 bg-white focus:border-navy"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="rounded-none border-silver h-12 bg-white focus:border-navy"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Organization Name *</label>
                    <Input
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      className="rounded-none border-silver h-12 bg-white focus:border-navy"
                      data-testid="contact-organization-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Practice Type</label>
                      <Select onValueChange={(value) => handleSelectChange('practiceType', value)}>
                        <SelectTrigger className="rounded-none border-silver h-12 bg-white" data-testid="contact-practice-type-select">
                          <SelectValue placeholder="Select practice type" />
                        </SelectTrigger>
                        <SelectContent>
                          {practiceTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Service Interest</label>
                      <Select onValueChange={(value) => handleSelectChange('service', value)}>
                        <SelectTrigger className="rounded-none border-silver h-12 bg-white" data-testid="contact-service-select">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>{service}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="rounded-none border-silver bg-white focus:border-navy resize-none"
                      placeholder="Tell us about your needs..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-navy hover:bg-navy-700 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8 w-full md:w-auto"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              )}
            </div>

            {/* Map / Image */}
            <div>
              <div className="bg-navy h-full min-h-[400px] flex flex-col justify-center p-10">
                <h3 className="font-manrope font-bold text-2xl text-white mb-4">
                  Schedule a Free Consultation
                </h3>
                <p className="text-navy-200 mb-8 leading-relaxed">
                  Our team of healthcare operations experts is ready to analyze your current processes and identify opportunities for improvement.
                </p>
                <ul className="space-y-4">
                  {[
                    'Free revenue cycle assessment',
                    'Custom implementation roadmap',
                    'ROI projection analysis',
                    'No obligation consultation',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white">
                      <CheckCircle className="w-5 h-5 text-emerald flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Locations */}
      <section className="section-padding bg-white" data-testid="locations-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-manrope font-bold text-2xl md:text-3xl text-navy">
              Our Office Locations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { city: 'Main Office - Brentwood, TN', address: '7100 Commerce Way Ste 182', zip: 'Brentwood, TN 37027', phone: '(855) 831-1992' },
              { city: 'Headquarters - Wilmington, DE', address: '919 N Market St Ste 600', zip: 'Wilmington, DE 19801', phone: '(855) 831-1992' },
            ].map((location) => (
              <Card 
                key={location.city}
                className="border-silver-light shadow-card"
                data-testid={`location-${location.city.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <CardContent className="p-6">
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-3">{location.city}</h3>
                  <p className="text-sm text-navy-600 mb-1">{location.address}</p>
                  <p className="text-sm text-navy-600 mb-3">{location.zip}</p>
                  <p className="text-sm text-emerald font-medium">{location.phone}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
